// 📍 src/pages/ThreatIntel.jsx
//
// Threat-intelligence dashboard for Oraku admin. Reads from `siem_events`
// (mirror of `security_alerts` enriched with geo data via the
// `flagSecurityAlert` helper in functions/index.js).
//
// What an admin sees:
//   1. KPI strip — total events (last 7d), unique attacking IPs, top
//      attack source country, # repeat offenders.
//   2. Top attack-source countries bar chart.
//   3. "Repeat offenders" table — IPs with > 5 events in 7d, sortable
//      by event count.
//   4. Attack-focus heatmap — what endpoints/actions are being targeted
//      most.
//   5. Recent events feed — last 100 events with country flag, action,
//      severity, masked IP.
//
// VPN / proxy / Tor flags are populated only when the geoLookup helper's
// upstream service (ipapi.co paid tier OR a future ipdata.co integration)
// returns those fields. On the free tier they're null and we just show
// "n/a" — the UI handles both gracefully.

import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, query, where, orderBy, limit, getDocs, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, StatCard, Card, Spinner } from '../components/UI';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';

const ACCENT = '#008751';
const RED    = '#ef4444';
const ORANGE = '#f97316';
const YELLOW = '#eab308';

const CHART_TOOLTIP = {
  contentStyle: {
    background: '#1A2332',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 10,
    fontSize: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  itemStyle: { color: '#fff' },
  labelStyle: { color: 'rgba(255,255,255,0.5)', marginBottom: 4 },
};

// 7d window for "recent" — long enough to spot patterns, short enough that
// the data fits in memory and renders fast.
const LOOKBACK_DAYS = 7;
const REPEAT_OFFENDER_THRESHOLD = 5; // events from same IP within window

export default function ThreatIntel() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadEvents(); }, []);

  async function loadEvents() {
    setLoading(true);
    try {
      const since = Timestamp.fromMillis(Date.now() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000);
      const q = query(
        collection(db, 'siem_events'),
        where('createdAt', '>=', since),  // Oraku writes `createdAt`
        orderBy('createdAt', 'desc'),
        limit(2000),
      );
      const snap = await getDocs(q);
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      // Fallback if the index isn't created yet — just pull recent events
      // and filter client-side.
      console.warn('siem_events query failed, falling back to scan:', e.message);
      try {
        const q = query(collection(db, 'siem_events'),
          orderBy('createdAt', 'desc'), limit(500));
        const snap = await getDocs(q);
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e2) {
        console.error('siem_events fallback also failed:', e2);
      }
    }
    setLoading(false);
  }

  const stats = useMemo(() => {
    const byCountry  = new Map();
    const byIp       = new Map();
    const byAction   = new Map();
    let vpnCount = 0;
    let torCount = 0;
    let proxyCount = 0;

    for (const ev of events) {
      const geo = ev.geo || {};
      const country = geo.country || 'Unknown';
      if (!byCountry.has(country)) {
        byCountry.set(country, { country, code: geo.countryCode, count: 0 });
      }
      byCountry.get(country).count++;

      const ip = geo.ipMasked || 'unknown';
      if (!byIp.has(ip)) {
        byIp.set(ip, {
          ipMasked: ip,
          country: country,
          countryCode: geo.countryCode,
          count: 0,
          actions: new Set(),
          isProxy: geo.isProxy,
          isVpn: geo.isVpn,
          isTor: geo.isTor,
          firstSeen: ev.createdAt,
          lastSeen: ev.createdAt,
        });
      }
      const ipBucket = byIp.get(ip);
      ipBucket.count++;
      ipBucket.actions.add(ev.action || ev.alertType || 'unknown');
      // Track first/last seen (createdAt is descending, so the FIRST iteration
      // gives us last-seen; track first-seen by overwriting).
      ipBucket.firstSeen = ev.createdAt;

      const action = ev.action || ev.alertType || 'unknown';
      if (!byAction.has(action)) byAction.set(action, { action, count: 0 });
      byAction.get(action).count++;

      if (geo.isVpn) vpnCount++;
      if (geo.isTor) torCount++;
      if (geo.isProxy) proxyCount++;
    }

    const countries = [...byCountry.values()].sort((a, b) => b.count - a.count);
    const ips       = [...byIp.values()].sort((a, b) => b.count - a.count);
    const actions   = [...byAction.values()].sort((a, b) => b.count - a.count);
    const repeatOffenders = ips.filter((ip) => ip.count >= REPEAT_OFFENDER_THRESHOLD);

    return {
      total: events.length,
      uniqueIps: ips.length,
      countries,
      ips,
      actions,
      repeatOffenders,
      topCountry: countries[0],
      vpnCount, torCount, proxyCount,
    };
  }, [events]);

  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <Spinner />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader
          title="Threat Intelligence"
          subtitle="Suspicious activity detected by Oraku Cloud Functions."
        />
        <Card>
          <div style={{ padding: 32, textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🛡️</div>
            <h3 style={{ color: '#fff', marginBottom: 8 }}>No threat events in the last 7 days</h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
              Either you're under no active attacks (most likely — congrats), or
              the SIEM pipeline hasn't fired yet for this period. Events are
              auto-captured on failed admin claims, gate-password failures,
              suspicious signups, and any other security signal flagged by
              Cloud Functions.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 32 }}>
      <PageHeader
        title="Threat Intelligence"
        subtitle={`${stats.total} events · ${stats.uniqueIps} unique IPs · last ${LOOKBACK_DAYS} days`}
      />

      {/* KPI Strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 16, marginBottom: 24,
      }}>
        <StatCard
          label="Total Events (7d)"
          value={stats.total.toLocaleString()}
          color={RED}
        />
        <StatCard
          label="Unique IPs"
          value={stats.uniqueIps.toLocaleString()}
          color={ORANGE}
        />
        <StatCard
          label="Top Source"
          value={stats.topCountry?.country || '—'}
          sub={stats.topCountry ? `${stats.topCountry.count} events` : ''}
          color={YELLOW}
        />
        <StatCard
          label="Repeat Offenders"
          value={stats.repeatOffenders.length}
          sub={`IPs with ≥${REPEAT_OFFENDER_THRESHOLD} events`}
          color={RED}
        />
        {(stats.vpnCount + stats.torCount + stats.proxyCount) > 0 && (
          <StatCard
            label="Anonymised Sources"
            value={stats.vpnCount + stats.torCount + stats.proxyCount}
            sub={`${stats.vpnCount} VPN · ${stats.torCount} Tor · ${stats.proxyCount} proxy`}
            color={'#a855f7'}
          />
        )}
      </div>

      {/* Top Attack-Source Countries */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
          letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Top Attack Source Countries
        </h3>
        <ResponsiveContainer width="100%" height={Math.max(240, 30 * Math.min(stats.countries.length, 10) + 60)}>
          <BarChart
            data={stats.countries.slice(0, 10)}
            layout="vertical"
            margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis type="number" stroke="rgba(255,255,255,0.5)" fontSize={11} />
            <YAxis type="category" dataKey="country" stroke="rgba(255,255,255,0.7)"
              fontSize={11} width={140} />
            <Tooltip {...CHART_TOOLTIP} />
            <Bar dataKey="count" fill={RED} radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Repeat Offenders Table */}
      {stats.repeatOffenders.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
            letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Repeat Offenders ({stats.repeatOffenders.length})
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={thStyle}>IP (masked)</th>
                <th style={thStyle}>Country</th>
                <th style={thStyle}>Anon</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Events</th>
                <th style={thStyle}>Targets</th>
              </tr>
            </thead>
            <tbody>
              {stats.repeatOffenders.slice(0, 50).map((ip) => (
                <tr key={ip.ipMasked} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ ...tdStyle, fontFamily: 'DM Mono, monospace' }}>{ip.ipMasked}</td>
                  <td style={tdStyle}>
                    {flagEmoji(ip.countryCode)} {ip.country}
                  </td>
                  <td style={tdStyle}>
                    {ip.isVpn ? '🟣 VPN' : ip.isTor ? '🟣 Tor' : ip.isProxy ? '🟣 Proxy' : ''}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: RED, fontWeight: 700 }}>
                    {ip.count}
                  </td>
                  <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.6)' }}>
                    {[...ip.actions].slice(0, 3).join(', ')}
                    {ip.actions.size > 3 ? ` +${ip.actions.size - 3}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Attack Focus — what endpoints are being hit most */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
          letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Attack Focus
        </h3>
        <div>
          {stats.actions.slice(0, 15).map((a) => (
            <div key={a.action} style={listRowStyle}>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{a.action}</span>
              <span style={{ color: ORANGE, fontWeight: 600 }}>{a.count}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent Events Feed */}
      <Card>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 16,
          letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Recent Events
        </h3>
        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
          {events.slice(0, 100).map((ev) => {
            const geo = ev.geo || {};
            return (
              <div key={ev.id} style={listRowStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>
                    {flagEmoji(geo.countryCode)} {ev.action || ev.alertType || 'unknown'}
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                    {geo.country || 'unknown'} · {geo.ipMasked || 'no-ip'}
                    {geo.isVpn && ' · VPN'}
                    {geo.isTor && ' · Tor'}
                    {geo.isProxy && ' · proxy'}
                    {' · '}{formatRelativeTime(ev.createdAt || ev.timestamp)}
                  </span>
                </div>
                {ev.severity && (
                  <span style={{
                    fontSize: 10, padding: '3px 8px', borderRadius: 4,
                    background: severityColor(ev.severity).bg,
                    color: severityColor(ev.severity).fg,
                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>{ev.severity}</span>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ── Style + helpers ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left',
  padding: '12px 12px',
  fontSize: 11,
  color: 'rgba(255,255,255,0.5)',
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};
const tdStyle = {
  padding: '12px 12px',
  color: 'rgba(255,255,255,0.85)',
  fontSize: 12,
};
const listRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 4px',
  fontSize: 13,
  color: 'rgba(255,255,255,0.85)',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
};

function flagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '';
  const offset = 0x1F1E6 - 0x41;
  return String.fromCodePoint(
    countryCode.toUpperCase().charCodeAt(0) + offset,
    countryCode.toUpperCase().charCodeAt(1) + offset,
  );
}

function severityColor(sev) {
  const s = (sev || '').toLowerCase();
  if (s === 'critical') return { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444' };
  if (s === 'high')     return { bg: 'rgba(249,115,22,0.15)', fg: '#f97316' };
  if (s === 'medium')   return { bg: 'rgba(234,179,8,0.15)', fg: '#eab308' };
  return { bg: 'rgba(255,255,255,0.06)', fg: 'rgba(255,255,255,0.5)' };
}

function formatRelativeTime(ts) {
  if (!ts) return '';
  const then = ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime());
  if (!then) return '';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(then).toLocaleDateString();
}
